/*
Component to render a box line between 2 absolute coordinates
using a base value to add extra height

  (x1, y1)
      |         (x2, y2)
      |            |
      |            |
      |____________|  base value (= absolute y coordinate)

  Inverted base
       ____________  base value
      |            |
      |            |
  (x1, y1)         |
                (x2, y2)
*/

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

export default class BoxLine extends React.Component {
  isCustomCSSColor(color) {
    return (color.charAt(0) === '#' ||
      color.startsWith('rgb(') ||
      color.startsWith('rgba(') ||
      color.startsWith('hsl(') ||
      color.startsWith('hsla('));
  }

  render() {
    let { x1, y1, x2, y2, thickness } = this.props;

    const { base, height, invertBase, lineLeft, lineRight, color } = this.props;

    if (thickness) {
      thickness = Math.round(thickness);
      thickness = `${thickness}px`;
    }

    x1 = Math.round(x1);
    y1 = Math.round(y1);
    x2 = Math.round(x2);
    y2 = Math.round(y2);

    const boxX = x1 < x2 ? x1 : x2;
    let boxY = y1 < y2 ? y1 : y2;
    const boxRight = x1 > x2 ? x1 : x2;
    let boxBottom = y1 > y2 ? y1 : y2;

    const diffY = boxBottom - boxY;
    let boxWidth = boxRight - boxX;

    if (boxWidth) {
      if (typeof (base) === 'number') {
        if (invertBase) {
          boxY = base < boxY ? base : boxY;
        } else {
          boxBottom = boxBottom < base ? base : boxBottom;
        }
      } else if (typeof (height) === 'number') {
        // In case the base value is not an absolute value but relative to the closest y-coordinate
        if (invertBase) {
          boxBottom -= height;
        } else {
          boxBottom += height;
        }
      }
    }

    const boxHeight = boxBottom - boxY;

    if (boxWidth === 0 && boxHeight === 0) {
      return null;
    }

    const swappedX = x1 > x2;
    const leftY = swappedX ? y2 : y1;
    const rightY = swappedX ? y1 : y2;

    let leftHeight = boxBottom - leftY;
    let rightHeight = boxBottom - rightY;

    if (invertBase) {
      leftHeight = leftY - boxY;
      rightHeight = rightY - boxY;
    }

    // Straight line going up or down
    // -> only render a vertical line (border-left), not showing any corner
    const isVerticalLine = boxWidth === 0;

    if (boxWidth < 1) {
      boxWidth = 1;
    }
    if (!isVerticalLine) {
      boxWidth = Math.round(boxWidth + 1);
    }

    const style = {
      width: `${boxWidth}px`,
      height: `${boxHeight}px`,
      left: `${boxX}px`,
      top: `${boxY}px`,
    };

    if (thickness) {
      style.borderWidth = thickness;
    }

    let colorClassName = null;

    const customCSSCol = this.isCustomCSSColor(color);
    if (customCSSCol) {
      style.borderColor = color;
    } else {
      colorClassName = color ? `box-line--${color}` : null;
    }

    if (diffY === 0 || isVerticalLine || leftHeight === 0 || rightHeight === 0) {
      const className = classnames(
        'box-line-container',
        'box-line',
        {
          'box-line--bottom': !isVerticalLine && !invertBase,
          'box-line--left': !isVerticalLine && lineLeft && leftHeight !== 0,
          'box-line--right': isVerticalLine || (lineRight && rightHeight !== 0),
          'box-line--top': !isVerticalLine && invertBase,
        },
        colorClassName,
      );

      return (
        <div className={className} style={style} />
      );
    }

    // Two separate halfs

    const className = classnames(
      'box-line-container',
    );

    const alignY = invertBase ? 'top' : 'bottom';

    const classNameLeft = classnames(
      'box-line',
      lineLeft ? 'box-line--left' : null,
      `box-line--${alignY}`,
      'box-line__left-half',
      `box-line__left-half--${alignY}`,
      (!customCSSCol) ? `box-line--${color}` : null,
    );

    const classNameRight = classnames(
      'box-line',
      lineRight ? 'box-line--right' : null,
      `box-line--${alignY}`,
      'box-line__right-half',
      `box-line__right-half--${alignY}`,
      (!customCSSCol) ? `box-line--${color}` : null,
    );

    const styleLeft = {
      height: leftHeight,
    };

    const styleRight = {
      height: rightHeight,
    };

    if (customCSSCol) {
      styleLeft.borderColor = color;
      styleRight.borderColor = color;
    }

    if (thickness) {
      styleLeft.borderWidth = thickness;
      styleRight.borderWidth = thickness;
    }

    return (
      <div className={className} style={style}>
        <div className={classNameLeft} style={styleLeft} />
        <div className={classNameRight} style={styleRight} />
      </div>
    );
  }
}

BoxLine.defaultProps = {
  x1: 0,
  y1: 0,
  x2: 100,
  y2: 0,
  color: 'gray',
  lineLeft: true,
  lineRight: true,
  thickness: 0, // A value of 0 will ignore this and use the border-width defined in the css class
};

BoxLine.propTypes = {
  x1: PropTypes.number,
  y1: PropTypes.number,
  x2: PropTypes.number,
  y2: PropTypes.number,

  lineLeft: PropTypes.bool,
  lineRight: PropTypes.bool,

  base: PropTypes.number, // fixed bottom
  height: PropTypes.number, // bottom follows y-position closest to the floor (probably still full of bugs)

  invertBase: PropTypes.bool,

  thickness: PropTypes.number,
  color: PropTypes.any, // hex value, rgb(a), hsl(a) or name defined in box-line.scss (&--<color>)
};



// WEBPACK FOOTER //
// ./js/components/configuration/box-line.jsx